import { useQueries, useQuery } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import { t } from 'i18next';

import { ConcreteMixture } from 'models';

type ConcreteMixturesResponse = {
    data: {
        concreteMixtures: {
            id: number,
            name: string
            co2Total: number
        }[];
    }
}

type CalibrationCurvesResponse = {
    data: {
        calibrationCurves: {
            id: number
            name: string
            minCalibrationCurveStrength: number
            maxCalibrationCurveStrength: number
        }[];
    }
}

type UseConcreteMixturesResult = {
    concreteMixtures: ConcreteMixture[];
    isLoading: boolean,
    errorMessage: string | null;
}

const fetchConcreteMixtures = async () => {
    const url = `${import.meta.env.VITE_APP_API_URL}/concrete-mixtures`;
    const response = await axios.get<ConcreteMixturesResponse>(url);
    return response.data;
};

const fetchCalibrationCurves = async (concreteMixtureId: number) => {
    const url = `${import.meta.env.VITE_APP_API_URL}/calibration-curves`;
    const response = await axios.get<CalibrationCurvesResponse>(url, { params: { concreteMixtureId } });
    return response.data;
};


export const useConcreteMixtures = (): UseConcreteMixturesResult => {
    const concreteMixturesQuery = useQuery<ConcreteMixturesResponse, Error>({
        queryKey: ['concreteMixtures'],
        queryFn: () => fetchConcreteMixtures(),
        staleTime: Infinity
    });

    const calibrationCurvesQueries = useQueries({
        queries: (concreteMixturesQuery.data?.data.concreteMixtures || []).map((concreteMixture) => ({
            queryKey: ['calibrationCurves', concreteMixture.id],
            queryFn: () => fetchCalibrationCurves(concreteMixture.id),
            staleTime: Infinity,
        })),
    });

    const concreteMixturesWithCalibrationCurves = concreteMixturesQuery.data?.data.concreteMixtures?.map((concreteMixture, index) => ({
        ...concreteMixture,
        calibrationCurves: calibrationCurvesQueries[index].data?.data.calibrationCurves.map(calibrationCurve => (
            {
                ...calibrationCurve,
            })),
    }));

    // Filter out all concrete mixtures without calibration curves
    const concreteMixtures: ConcreteMixture[] = concreteMixturesWithCalibrationCurves?.filter(concreteMixture => {
        return concreteMixture.calibrationCurves !== undefined && concreteMixture.calibrationCurves.length > 0;
    }).map(concreteMixture => concreteMixture as ConcreteMixture) || [];

    const isLoading = concreteMixturesQuery.isLoading || calibrationCurvesQueries.some((query) => query.isLoading);

    const error = concreteMixturesQuery.error || calibrationCurvesQueries.find((query) => query.error)?.error || null;
    const errorMessage = error && (isAxiosError(error) ? error.message : t('errorLoadingConcreteMixtures'));

    return {
        concreteMixtures,
        isLoading,
        errorMessage
    };
};